// Formatting helpers shared by the pages; pure, unit-tested.

const dateTime = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
  hour12: false,
});

const dateOnly = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** `22 Oct 2026, 18:05 UTC` from an RFC 3339 string; the input when it does not parse. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${dateTime.format(d)} UTC`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dateOnly.format(d);
}

/** `1:56` from minutes (hours and minutes, no seconds). */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}:${rest.toString().padStart(2, "0")}`;
}

/** `12.5 h` from hours with one decimal; `0 h` for nothing. */
export function formatHours(hours: number | null | undefined): string {
  if (!hours) return "0 h";
  return `${(Math.round(hours * 10) / 10).toLocaleString("en-US", { maximumFractionDigits: 1 })} h`;
}

/** Seconds of a counter as `h:mm` (APU hobbs, ISI run time). */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "n/a";
  return formatMinutes(seconds / 60);
}

/** `in 7 min`, `3 h ago`, `2 days ago`; relative to `now` for tests. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = (d.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diff);
  const unit = abs < 60 ? ["s", 1] : abs < 3600 ? ["min", 60] : abs < 86400 ? ["h", 3600] : ["days", 86400];
  const n = Math.round(abs / (unit[1] as number));
  const label = unit[0] === "days" && n === 1 ? "day" : unit[0];
  return diff >= 0 ? `in ${n} ${label}` : `${n} ${label} ago`;
}

/** "9 min", "2 h", "3 days": how long is left until `iso`, nothing when it has passed. */
export function formatRemaining(iso: string, now: Date = new Date()): string {
  const left = formatRelative(iso, now);
  return left.startsWith("in ") ? left.slice(3) : "no time";
}

/** `45.123, -122.456` */
export function formatPosition(pos: { lat?: number; lon?: number } | null | undefined): string {
  if (!pos || typeof pos.lat !== "number" || typeof pos.lon !== "number") return "unknown";
  return `${pos.lat.toFixed(3)}, ${pos.lon.toFixed(3)}`;
}

export type LeaseLike = { holder: { handle: string; name?: string | null }; expires_at: string; acquired_at: string; flying?: boolean } | null | undefined;

/** The one line RF-12 asks for: free, or who has it and since when. */
export function leaseStatus(lease: LeaseLike, now: Date = new Date()): { label: string; tone: "ok" | "warn" } {
  if (!lease) return { label: "Available", tone: "ok" };
  // Flying: the holder's simulator has the aircraft loaded now (decision 071).
  return {
    label: `Booked${lease.flying ? ", flying" : ""} by ${lease.holder.name || lease.holder.handle}, ${formatRelative(lease.acquired_at, now)}`,
    tone: "warn",
  };
}

/** The tone of a condition level (F413): green up to 2, amber at 3, red at 4. */
export function levelTone(level: number | null | undefined): "ok" | "warn" | "bad" {
  const n = level ?? 0;
  return n >= 4 ? "bad" : n >= 3 ? "warn" : "ok";
}

/// A condition index (F402): the percentage and the level the plugin draws.
export function formatLevel(ratio: number | null | undefined, level: number | null | undefined): string {
  const pct = Math.round(Math.max(0, Math.min(1, ratio ?? 0)) * 100);
  return `${pct}% (level ${level ?? 0})`;
}

/// "12 days ago by marco, 14 flights ago", or "never".
/** How much of a thing there is, in a word: nothing under 2 %, then light, fair, heavy, thick by the quarter. */
export function amountWord(ratio: number | null | undefined): string | null {
  if (ratio == null || !Number.isFinite(ratio) || ratio < 0.02) return null;
  if (ratio < 0.25) return "light";
  if (ratio < 0.5) return "fair";
  if (ratio < 0.75) return "heavy";
  return "thick";
}

/**
 * The windshield's line under its bar (decision 103, F490): the bugs and
 * the stains of the weather it sat through told apart, and the last span
 * outside. "bugs light; dust fair, spots light after 9 days outside at
 * LOWI, 5 rainy hours"; "clean" when nothing is on it. Since 2026-11-03 (decision
 * 103 amended) the days the aircraft sits now follow, when `parkedNow`: they are
 * charged at the next booking, so the reader is not misled by the last span alone.
 */
export function windshieldLine(
  c: {
    windshield_bugs: number;
    windshield_dust?: number | null;
    windshield_spots?: number | null;
    windshield_streaks?: number | null;
    parked?: { days: number; rain_hours: number; snow_hours: number; icao?: string | null; since?: string | null } | null;
  },
  now: Date = new Date(),
  parkedNow = true,
): string {
  const bugs = amountWord(c.windshield_bugs);
  const stains = [
    ["dust", amountWord(c.windshield_dust)],
    ["spots", amountWord(c.windshield_spots)],
    ["streaks", amountWord(c.windshield_streaks)],
  ].filter((s): s is [string, string] => s[1] !== null);
  const parts: string[] = [];
  parts.push(bugs ? `bugs ${bugs}` : "no bugs");
  if (stains.length > 0) parts.push(stains.map(([what, how]) => `${what} ${how}`).join(", "));
  let line = parts.join("; ");
  if (!bugs && stains.length === 0) line = "clean";
  const p = c.parked;
  if (p && p.days >= 0.5) {
    const days = p.days >= 10 ? Math.round(p.days) : Math.round(p.days * 10) / 10;
    const wet: string[] = [];
    if (p.rain_hours >= 1) wet.push(`${Math.round(p.rain_hours)} rainy hour${Math.round(p.rain_hours) === 1 ? "" : "s"}`);
    if (p.snow_hours >= 1) wet.push(`${Math.round(p.snow_hours)} of snow`);
    line += ` after ${days} day${days === 1 ? "" : "s"} outside${p.icao ? ` at ${p.icao}` : ""}${wet.length ? `, ${wet.join(", ")}` : ""}`;
  }
  if (p?.since && parkedNow) {
    const soFar = Math.max(0, (now.getTime() - new Date(p.since).getTime()) / 86_400_000);
    const shown = soFar >= 10 ? Math.round(soFar) : Math.round(soFar * 10) / 10;
    line += `; parked since ${formatDate(p.since)}, ${shown} day${shown === 1 ? "" : "s"} so far, charged at the next booking`;
  }
  return line;
}

export function formatMaintenance(
  record: { at: string; by?: { handle: string; name?: string | null } | null; flights_since: number } | null | undefined,
  now: Date = new Date(),
): string {
  if (!record) return "never";
  const who = record.by ? ` by ${record.by.name || record.by.handle}` : "";
  const flights = record.flights_since === 1 ? "1 flight ago" : `${record.flights_since} flights ago`;
  return `${formatRelative(record.at, now)}${who}, ${flights}`;
}

/** The maintenance jobs (F406, decision 048): what they are in real life and how long they take. */
export const MAINTENANCE = [
  { action: "wash_exterior", label: "Wash the exterior", duration: "90 s", hint: "A ground-service job: the aircraft parked with the engines off, the air above freezing, no rain" },
  { action: "clean_windshield", label: "Clean the windshield", duration: "10 s", hint: "The crew's job at the walkaround: the aircraft parked with the engines off" },
  { action: "clean_cabin", label: "Clean the cabin", duration: "30 s", hint: "The cleaning service: the aircraft parked with the engines off, the main door open" },
  { action: "wipe_cockpit", label: "Wipe the cockpit", duration: "10 s", hint: "The crew's wipe at the walkaround: the aircraft parked with the engines off; the ground-in grime stays" },
  { action: "detail_cockpit", label: "Detail the cockpit", duration: "60 s", hint: "The detailing service: the aircraft parked with the engines off, the main door open; every control clean" },
] as const;

/** The components the crew writes a defect on (F448): the page's own, in words. */
export const CREW_COMPONENTS = [
  { component: "airframe", label: "Airframe" },
  { component: "engine_1", label: "Engine 1" },
  { component: "engine_2", label: "Engine 2" },
  { component: "engines", label: "Both engines" },
  { component: "engine_oil", label: "Engine oil" },
  { component: "apu", label: "APU" },
  { component: "tyres", label: "Tyres" },
  { component: "brakes", label: "Brakes" },
  { component: "gear", label: "Landing gear" },
  { component: "batteries", label: "Batteries" },
  { component: "hydraulics", label: "Hydraulics" },
  { component: "electrical", label: "Electrical" },
  { component: "lighting", label: "Lighting" },
  { component: "pneumatics", label: "Pneumatics" },
  { component: "pressurisation", label: "Pressurisation" },
  { component: "fuel", label: "Fuel" },
  { component: "ice_protection", label: "Ice protection" },
  { component: "flight_controls", label: "Flight controls" },
  { component: "oxygen", label: "Oxygen" },
  { component: "fire_protection", label: "Fire protection" },
  { component: "avionics", label: "Avionics" },
  { component: "cabin", label: "Cabin" },
] as const;

/** The component jobs (F431b, decision 069), run from the maintenance page. */
export const COMPONENT_JOBS = [
  { action: "change_tyres", label: "Change the tyres", duration: "90 s", hint: "A line-maintenance job: the aircraft parked with the engines off; the tyres' count starts again" },
  { action: "change_brakes", label: "Change the brakes", duration: "120 s", hint: "A line-maintenance job: the aircraft parked with the engines off; the brakes' count starts again" },
  { action: "inspect_gear", label: "Inspect the gear", duration: "60 s", hint: "After a hard landing: the aircraft parked with the engines off; closes the gear's entries" },
  { action: "inspect_engine_1", label: "Inspect engine 1", duration: "120 s", hint: "A look through the nozzle after an exceedance: the aircraft parked with the engines off" },
  { action: "inspect_engine_2", label: "Inspect engine 2", duration: "120 s", hint: "A look through the nozzle after an exceedance: the aircraft parked with the engines off" },
  { action: "service_batteries", label: "Service the batteries", duration: "30 s", hint: "The batteries charged and checked: the aircraft parked with the engines off" },
  { action: "check_airframe", label: "Airframe check", duration: "180 s", hint: "The programme's check by hours and calendar: the aircraft parked with the engines off; the counters start again" },
  { action: "repair_hydraulics", label: "Repair the hydraulics", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "repair_electrical", label: "Repair the electrics", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "repair_pneumatics", label: "Repair the pneumatics", duration: "240 s", hint: "A shop job on the bleed air and the pressurisation: its faults cleared on the aircraft at the next load" },
  { action: "repair_fuel_system", label: "Repair the fuel system", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "repair_ice_protection", label: "Repair the ice protection", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "repair_flight_controls", label: "Repair the flight controls", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "service_oxygen", label: "Service the oxygen", duration: "60 s", hint: "The bottle refilled on the aircraft at the next load through its own ground oxygen service, the masks checked; the system's faults cleared with it" },
  { action: "repair_fire_protection", label: "Repair the fire protection", duration: "240 s", hint: "A shop job on the loops and the bottles: the bottles serviced on the aircraft at the next load through its own command, its faults cleared with them" },
  { action: "repair_avionics", label: "Repair the avionics", duration: "240 s", hint: "A shop job on the system: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "repair_apu", label: "Repair the APU", duration: "240 s", hint: "A shop job on the APU: its faults cleared on the aircraft at the next load, its entries closed" },
  { action: "replenish_oil", label: "Replenish the oil", duration: "15 s", hint: "Both engines topped up at the next load through the aircraft's own oil service panel in the aft equipment bay; the oil entries closed" },
  { action: "restow_adg", label: "Restow the air-driven generator", duration: "30 s", hint: "After a deployment in flight: restowed on the aircraft at the next load through its own command; the aircraft parked with the engines off" },
  { action: "overhaul_gear", label: "Overhaul the landing gear", duration: "600 s", hint: "The gear off the aircraft to the shop, the wheels and the brakes with it: the 96-month restoration done, the 192-month overhaul when due; the aircraft parked with the engines off" },
  { action: "shop_visit_engine_1", label: "Engine 1 shop visit", duration: "900 s", hint: "The engine off the wing to the shop: its life-limited parts replaced, the borescope done, the oil full; the aircraft parked with the engines off" },
  { action: "shop_visit_engine_2", label: "Engine 2 shop visit", duration: "900 s", hint: "The engine off the wing to the shop: its life-limited parts replaced, the borescope done, the oil full; the aircraft parked with the engines off" },
] as const;

export function maintenanceLabel(action: string): string {
  switch (action) {
    case "wash_exterior":
      return "exterior wash";
    case "clean_windshield":
      return "windshield cleaning";
    case "change_tyres":
      return "tyre change";
    case "change_brakes":
      return "brake change";
    case "inspect_gear":
      return "gear inspection";
    case "inspect_engine_1":
      return "engine 1 inspection";
    case "inspect_engine_2":
      return "engine 2 inspection";
    case "service_batteries":
      return "battery service";
    case "check_airframe":
      return "airframe check";
    case "clean_cabin":
      return "cabin cleaning";
    case "wipe_cockpit":
      return "cockpit wipe";
    case "detail_cockpit":
      return "cockpit detailing";
    case "repair_hydraulics":
      return "hydraulic system repair";
    case "repair_electrical":
      return "electrical repair";
    case "repair_pneumatics":
      return "pneumatic and pressurisation repair";
    case "repair_fuel_system":
      return "fuel system repair";
    case "repair_ice_protection":
      return "ice protection repair";
    case "repair_flight_controls":
      return "flight control repair";
    case "service_oxygen":
      return "oxygen service";
    case "repair_fire_protection":
      return "fire protection repair";
    case "repair_avionics":
      return "avionics repair";
    case "repair_apu":
      return "APU repair";
    case "replenish_oil":
      return "oil replenishment";
    case "restow_adg":
      return "ADG restow";
    case "overhaul_gear":
      return "landing gear overhaul";
    case "shop_visit_engine_1":
      return "engine 1 shop visit";
    case "shop_visit_engine_2":
      return "engine 2 shop visit";
    default:
      return action;
  }
}

/// "cabin cleaning by marco, done in 12 s" (or "done any moment now").
export function formatInProgress(
  job: { action: string; completes_at: string; by?: { handle: string; name?: string | null } | null },
  now: Date = new Date(),
): string {
  const who = job.by ? ` by ${job.by.name || job.by.handle}` : "";
  const left = Math.ceil((new Date(job.completes_at).getTime() - now.getTime()) / 1000);
  const when = left <= 0 ? "done any moment now" : `done in ${left} s`;
  return `${maintenanceLabel(job.action)}${who}, ${when}`;
}

/** One timeline entry in words (F411): who did what, and for a refused check-in which rule and why. */
export function eventSummary(e: { type: string; actor?: string | null; payload?: unknown }): string {
  const who = e.actor ?? "";
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const text = (k: string) => (typeof p[k] === "string" ? (p[k] as string) : "");
  switch (e.type) {
    case "version.refused":
      return `${who || "a pilot"}'s return refused: ${text("code") || "a rule"}${text("message") ? ` (${text("message")})` : ""}`;
    case "version.committed":
      return `returned by ${who || "someone"}, entry ${typeof p.seq === "number" ? p.seq : "?"}`;
    case "lease.acquired":
      return `booked by ${who || "someone"}`;
    case "lease.released":
      // The simulator lost the flight (F494): the booking closed by the hold, nothing written.
      if (text("reason") === "simulator_lost") return `${who || "the pilot"}'s simulator lost the flight: the booking closed, nothing written, the aircraft as before it`;
      return `made available again by ${who || "someone"}`;
    case "lease.expired":
      // The booking's limit or the heartbeats, whichever came first (decision 130).
      return text("reason") === "limit" ? "the booking reached its limit: the aircraft is available again" : "the time ran out: the aircraft is available again";
    case "lease.revoked":
      return `taken back by ${who || "a manager"}`;
    case "lease.preflight_signed":
      return `the pre-flight signed by ${who || "the pilot"}${text("note") ? `: "${text("note")}"` : ""}`;
    case "lease.cockpit_shared":
      return `the shared cockpit's key shared by ${who || "the pilot"}`;
    case "lease.cockpit_withdrawn":
      return `the shared cockpit's key withdrawn by ${who || "someone"}`;
    case "airframe.published":
      return `shared by ${who || "someone"}`;
    case "conflict.created":
      return `a return held for the owner, by ${who || "someone"}`;
    case "conflict.accepted":
      return `a held return kept by ${who || "someone"}`;
    case "conflict.discarded":
      return `a held return discarded by ${who || "someone"}`;
    case "flight.track_refused":
      return `the recorder's track refused: ${text("reason") || "a rule"}`;
    case "airframe.isi_replaced":
      return `the standby instrument replaced: serial ${text("from") || "?"} to ${text("to") || "?"}${who ? ` (by ${who})` : ""}`;
    case "version.rolled_back":
      return `entry ${typeof p.restores_seq === "number" ? p.restores_seq : "?"} restored as entry ${typeof p.seq === "number" ? p.seq : "?"} by ${who || "someone"}`;
    case "maintenance.performed":
      return `${maintenanceLabel(text("action"))} by ${who || "someone"}`;
    default: {
      // "member.joined" by anna reads "member joined by anna".
      const words = e.type.replace(/[._]/g, " ");
      return who ? `${words} by ${who}` : words;
    }
  }
}

/** A pilot's name as the account page and the sidebar show it (F414): the first and last name, else Discord's display name, else the handle. */
/**
 * The visibility in the pilots' words, as the settings page offers it: the
 * setting's own name "unlisted" never reaches a page, that word is the
 * unlisting's (decision 138; 2026-11-04: an aircraft by link only read as
 * still unlisted after it was put back).
 */
export function visibilityWord(visibility: string): string {
  switch (visibility) {
    case "public":
      return "public";
    case "unlisted":
      return "by link only";
    case "hidden":
      return "members only";
    default:
      return visibility;
  }
}

export function pilotName(user: { first_name?: string | null; last_name?: string | null; display_name?: string | null; handle: string }): string {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.display_name || user.handle;
}

/** A limit's code in words (F417). */
export function exceedanceLabel(code: string): string {
  switch (code) {
    case "overspeed":
      return "Overspeed";
    case "mach_overspeed":
      return "Mach overspeed";
    case "flap_overspeed":
      return "Flap overspeed";
    case "gear_overspeed":
      return "Gear overspeed";
    case "load_factor":
      return "Load factor";
    case "bank":
      return "Steep bank";
    case "altitude":
      return "Above the ceiling";
    case "itt":
      return "Engine too hot";
    case "sink_rate":
      return "Unstable approach";
    default:
      return code.replace(/_/g, " ");
  }
}

/** The tone of a touchdown's severity (F417). */
export function landingTone(severity: string | null | undefined): "ok" | "warn" | "bad" {
  return severity === "hard" ? "bad" : severity === "firm" ? "warn" : "ok";
}

/** One seat of a flight's crew as the API serves it (F475). */
export type FlightSeat = { handle?: string | null; name?: string | null; display_name?: string | null; guest: boolean; role: number; pilot_seat: number; yours?: boolean };

/** Who sits in a seat: "you", the name the pilot shows, or "a pilot who keeps their name private". */
export function seatWho(seat: FlightSeat): string {
  return seat.yours ? "you" : seat.name || seat.handle || "a pilot who keeps their name private";
}

/** A seat's words: "medtner, captain in the left seat", "you, pilot in the right seat", "a pilot who keeps their name private, passenger". */
export function seatLabel(seat: FlightSeat): string {
  return `${seatWho(seat)}, ${crewRoleLabel(seat.role, seat.pilot_seat)}`;
}

/**
 * Who was aboard a flight, told apart (2026-11-03, Gianluca: "it is not
 * clear who was flying the airplane and who was the passenger"): the pilot
 * flying (the host of the shared cockpit with their seat, else the pilot
 * the flight is attributed to), the others in the cockpit with their seats,
 * the passengers by name. `reported` is false when nobody reported a seat:
 * the flight may have carried passengers the platform never heard of.
 */
export type PeopleAboard = { flying: string | null; cockpit: string[]; passengers: string[]; reported: boolean };
export function peopleAboard(pilot: string | null | undefined, crew: FlightSeat[]): PeopleAboard {
  const host = crew.find((s) => !s.guest);
  const guests = crew.filter((s) => s.guest);
  return {
    flying: host ? seatLabel(host) : (pilot ?? null),
    cockpit: guests.filter((s) => s.role > 0).map(seatLabel),
    passengers: guests.filter((s) => s.role === 0).map(seatWho),
    reported: crew.length > 0,
  };
}

/** The crew's line for a flight: the host first, then the guests aboard. Empty when nobody reported. */
export function crewLine(crew: FlightSeat[]): string {
  return crew.map(seatLabel).join("; ");
}

/** The caller's own seat on a flight they did not fly, if any. */
export function yourSeat(crew: FlightSeat[]): FlightSeat | null {
  return crew.find((s) => s.yours && s.guest) ?? null;
}

/** The words for a seat in a shared cockpit (F470, decision 093; the plugin's window says the same): the CL650's own numbers. */
export function crewRoleLabel(role: number, pilotSeat: number): string {
  const seat = pilotSeat === 0 ? "left seat" : pilotSeat === 1 ? "right seat" : `seat ${pilotSeat}`;
  switch (role) {
    case 0:
      return "passenger";
    case 1:
      return `pilot in the ${seat}`;
    case 2:
      return `captain in the ${seat}`;
    default:
      return `role ${role} in the ${seat}`;
  }
}

/** `m:ss` from seconds, `h:mm:ss` past an hour. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${rest.toString().padStart(2, "0")}` : `${m}:${rest.toString().padStart(2, "0")}`;
}

export function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
